// frontend/packages/business-console/src/services/workflowFormService.js
//
// Work item 5.2 — thin wrapper over the 4.7 Console-auth submit endpoint
// (`POST /api/v2/workflow-forms/:formIdOrSlug/submit`).
//
// Business context comes from the x-business-id header (axios
// interceptor in @upryzze/services); callers don't pass businessId.
//
// `idempotencyKey` is wired so the same renderer-generated UUID
// flows: as the request-level Idempotency-Key (1.5 Redis middleware
// short-circuits in-flight retries) AND in the body's `clientUuid`
// (the durable Mongo unique index on `(businessId, clientUuid)` from
// 4.3 catches everything else). Two-layer dedup matches the 2.19
// quote / invoice path; the future workflow-form renderer drops the
// same `clientUuid` into both slots.

import { api, getApiBase, buildTenantAuthHeaders } from '@upryzze/services';

/**
 * List currently-published workflow forms for the active business.
 * Drafts and superseded older versions are filtered out server-side
 * (work item 4.6). Returns the latest published version per slug.
 *
 * @param {object} [params]
 * @param {number} [params.limit] — 1..200, default 50.
 * @returns {Promise<{ items: Array<{
 *   _id: string,
 *   slug: string,
 *   name: string,
 *   version: number,
 *   compiledSchema: object,
 *   mapsTo: Array<object>,
 *   attachmentsConfig: object|null,
 *   publishedAt: string,
 *   createdAt: string,
 *   updatedAt: string,
 * }> }>}
 */
export async function listWorkflowForms({ limit } = {}) {
  const { data } = await api.get('/v2/workflow-forms', {
    params: limit ? { limit } : undefined,
  });
  return data || { items: [] };
}

/**
 * Fetch a single published workflow form by `_id` or `slug` (slug
 * resolves to the latest published version). Returns the compiled
 * schema — `jsxSource` is NEVER exposed via this endpoint (work item
 * 4.6 security note).
 */
export async function getWorkflowForm(formIdOrSlug) {
  const { data } = await api.get(
    `/v2/workflow-forms/${encodeURIComponent(formIdOrSlug)}`
  );
  return data;
}

/**
 * Request a short-TTL SAS upload URL for a workflow-form attachment
 * (work item 4.8). The caller then PUTs the file directly to Azure
 * Blob using the returned `uploadUrl`, and includes the response's
 * `{fieldId, blobUrl, originalName, sizeBytes, mimeType, scanStatus:
 * 'pending'}` shape in the submit payload's `attachments[]`.
 *
 * @returns {Promise<{
 *   container: string,
 *   path: string,
 *   blobUrl: string,
 *   uploadUrl: string,
 *   expiresAt: string,
 *   fieldId: string,
 *   originalName: string,
 *   sizeBytes: number,
 *   mimeType: string,
 * }>}
 */
export async function requestWorkflowFormUploadToken(
  formIdOrSlug,
  { fieldId, clientUuid, filename, sizeBytes, mimeType }
) {
  const { data } = await api.post(
    `/v2/workflow-forms/${encodeURIComponent(formIdOrSlug)}/attachments/upload-token`,
    { fieldId, clientUuid, filename, sizeBytes, mimeType }
  );
  return data;
}

// ── 5.3 — chunked, resumable upload ─────────────────────────────────
//
// Field reps upload photos over flaky cell connections. A single PUT
// restarts the whole transfer on any drop, and the 15-min SAS (4.8) can
// expire mid-upload. `uploadBlobResumable` solves both by using Azure
// Block Blob staged uploads:
//
//   1. split the file into fixed-size blocks (`Put Block`);
//   2. commit them in one `Put Block List`.
//
// Resumability: each successful block is tracked locally; a retry skips
// blocks already staged. Staged-but-uncommitted blocks live ~7 days
// server-side keyed by the *blob path* (stable: businessId/formId/
// clientUuid/filename per 4.8), so when a SAS expires mid-upload we
// transparently re-mint a fresh one via `refreshToken` and keep going —
// "replays via fresh SAS request on each attempt" (5.3). Same `cw`
// (create+write) SAS permission as the single-PUT path — no backend
// change. Small files (≤ one block) keep the fast single-PUT, still
// SAS-refresh aware.

const RESUMABLE_BLOCK_SIZE = 4 * 1024 * 1024; // 4 MB
const MAX_OP_ATTEMPTS = 4;

function toBase64(str) {
  if (typeof btoa === 'function') return btoa(str);
  // Node fallback (unit tests run outside the browser).
  return Buffer.from(str, 'binary').toString('base64');
}

// Azure requires every block ID within a blob to be the same length,
// so encode a fixed-width index.
function blockIdForIndex(index) {
  return toBase64(`blk-${String(index).padStart(8, '0')}`);
}

function commitBlockListXml(ids) {
  const latest = ids.map((id) => `<Latest>${id}</Latest>`).join('');
  return `<?xml version="1.0" encoding="utf-8"?><BlockList>${latest}</BlockList>`;
}

// uploadUrl already carries the SAS query (`?sv=...&sig=...`); append
// the operation params with `&` (comp/blockid are NOT part of the SAS
// signature — the write permission authorizes the block ops).
function withQuery(url, extra) {
  return `${url}&${extra}`;
}

function backoffMs(attempt) {
  return Math.min(8000, 250 * 2 ** (attempt - 1));
}

// PUT with: one transparent SAS refresh on 403 (expired/invalid SAS),
// plus bounded backoff retry on network errors and 5xx/408. `ctx` holds
// the current uploadUrl/blobUrl so a refresh is visible to later ops.
async function putWithRetry(ctx, buildUrl, fetchOpts) {
  let attempts = 0;
  let refreshed = false;
  for (;;) {
    let res;
    try {
      res = await ctx.fetchImpl(buildUrl(ctx.uploadUrl), fetchOpts);
    } catch (err) {
      attempts += 1;
      if (attempts >= ctx.maxAttempts) throw err;
      await ctx.sleep(backoffMs(attempts));
      continue;
    }
    if (res.ok) return res;
    if (res.status === 403 && !refreshed && ctx.refreshToken) {
      const t = await ctx.refreshToken();
      if (t && t.uploadUrl) {
        ctx.uploadUrl = t.uploadUrl;
        if (t.blobUrl) ctx.blobUrl = t.blobUrl;
        refreshed = true;
        continue; // immediate retry on the fresh SAS; doesn't burn budget
      }
    }
    if ((res.status >= 500 || res.status === 408) && attempts < ctx.maxAttempts - 1) {
      attempts += 1;
      await ctx.sleep(backoffMs(attempts));
      continue;
    }
    const text = await res.text().catch(() => '');
    const e = new Error(
      `Azure Blob upload failed (${res.status}): ${String(text).slice(0, 200)}`
    );
    e.status = res.status;
    throw e;
  }
}

/**
 * Chunked, resumable upload of `file` to Azure Blob via a SAS.
 *
 * @param {File|Blob} file
 * @param {object} opts
 * @param {{uploadUrl:string, blobUrl:string}} [opts.token] — initial SAS
 *   from `requestWorkflowFormUploadToken`.
 * @param {() => Promise<{uploadUrl:string, blobUrl:string}>} [opts.refreshToken]
 *   — re-mints a fresh SAS for the SAME blob path when the current one
 *   expires (and is used to fetch the first token if `token` is omitted).
 * @param {number} [opts.blockSize] — bytes per block (default 4 MB).
 * @param {(uploaded:number, total:number) => void} [opts.onProgress]
 * @param {Set<number>} [opts.completed] — indices already staged; lets a
 *   caller resume a partial upload across sessions. Mutated in place.
 * @param {(index:number, completed:Set<number>) => void} [opts.onBlockComplete]
 * @param {typeof fetch} [opts.fetchImpl] — injectable for tests.
 * @param {(ms:number) => Promise<void>} [opts.sleep] — injectable for tests.
 * @returns {Promise<string>} the durable `blobUrl` (no SAS query).
 */
export async function uploadBlobResumable(file, opts = {}) {
  const blockSize = opts.blockSize || RESUMABLE_BLOCK_SIZE;
  const ctx = {
    uploadUrl: opts.token?.uploadUrl || null,
    blobUrl: opts.token?.blobUrl || null,
    refreshToken: opts.refreshToken || null,
    fetchImpl: opts.fetchImpl || fetch,
    sleep: opts.sleep || ((ms) => new Promise((r) => setTimeout(r, ms))),
    maxAttempts: opts.maxAttempts || MAX_OP_ATTEMPTS,
  };
  if (!ctx.uploadUrl) {
    if (!ctx.refreshToken) {
      throw new Error('uploadBlobResumable requires a token or refreshToken');
    }
    const t = await ctx.refreshToken();
    ctx.uploadUrl = t.uploadUrl;
    ctx.blobUrl = t.blobUrl;
  }

  const total = file.size;
  const contentType = file.type || 'application/octet-stream';

  // Fast path: a small file is a single block — one PUT, still SAS-aware.
  if (total <= blockSize) {
    await putWithRetry(ctx, (url) => url, {
      method: 'PUT',
      headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': contentType },
      body: file,
    });
    opts.onProgress?.(total, total);
    return ctx.blobUrl;
  }

  // Chunked path: stage each block, then commit the block list.
  const blockCount = Math.ceil(total / blockSize);
  const completed = opts.completed instanceof Set ? opts.completed : new Set();
  const blockIds = [];
  let uploaded = 0;
  for (let i = 0; i < blockCount; i += 1) {
    const id = blockIdForIndex(i);
    blockIds.push(id);
    const start = i * blockSize;
    const end = Math.min(start + blockSize, total);
    if (completed.has(i)) {
      uploaded += end - start;
      opts.onProgress?.(uploaded, total);
      continue; // resume: already staged
    }
    await putWithRetry(
      ctx,
      (url) => withQuery(url, `comp=block&blockid=${encodeURIComponent(id)}`),
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: file.slice(start, end),
      }
    );
    completed.add(i);
    opts.onBlockComplete?.(i, completed);
    uploaded += end - start;
    opts.onProgress?.(uploaded, total);
  }

  await putWithRetry(ctx, (url) => withQuery(url, 'comp=blocklist'), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/xml',
      'x-ms-blob-content-type': contentType,
    },
    body: commitBlockListXml(blockIds),
  });
  return ctx.blobUrl;
}

/**
 * List workflow-form submissions for the active business (4.11b).
 * Filters: `customerId`, `formId`, `before` (ISO cursor), `limit`.
 * Returns list-shape rows (no `values` or `attachments`) — use
 * `getWorkflowFormSubmission(id)` for the full detail.
 */
export async function listWorkflowFormSubmissions(params = {}) {
  const { data } = await api.get('/v2/workflow-forms/submissions', {
    params,
  });
  return data || { items: [], hasMore: false, nextCursor: null };
}

/**
 * Fetch a workflow-form submission with full `values` +
 * `attachments` + the matching form-definition's compiledSchema for
 * field-label resolution.
 */
export async function getWorkflowFormSubmission(id) {
  const { data } = await api.get(
    `/v2/workflow-forms/submissions/${encodeURIComponent(id)}`
  );
  return data;
}

/**
 * Render (or reuse the cached) PDF service-visit report for a
 * submission (4.15). Returns `{ blobUrl, renderedAt, cached }`. Pass
 * `force: true` to bypass the cache and re-render.
 */
export async function renderWorkflowFormReport(id, { force } = {}) {
  const { data } = await api.post(
    `/v2/workflow-forms/submissions/${encodeURIComponent(id)}/render${
      force ? '?force=1' : ''
    }`
  );
  return data;
}

/**
 * Get a short-TTL signed download URL for the rendered report (4.16).
 * 409s if the report hasn't been rendered yet — callers should render
 * first. Returns `{ url, expiresOn }`.
 */
export async function getWorkflowFormReportDownloadUrl(id) {
  const { data } = await api.get(
    `/v2/workflow-forms/submissions/${encodeURIComponent(id)}/report`
  );
  return data;
}

/**
 * Email the rendered report to the submission's linked customer
 * (4.16). Auto-renders if needed. Returns `{ sent, to }`. 400 if the
 * submission has no linked customer or the customer has no email.
 */
export async function emailWorkflowFormReport(id) {
  const { data } = await api.post(
    `/v2/workflow-forms/submissions/${encodeURIComponent(id)}/email`
  );
  return data;
}

/**
 * Operator override of an attachment's `scanStatus` (work item
 * 4.13). businessadmin / manager only — the backend 403s for
 * employees. Audit-logged. The future automated-scanner worker
 * uses the SAME backend endpoint, just with `actorType: 'system'`.
 *
 * @param {string} submissionId — 24-hex ObjectId of the submission
 * @param {object} body
 * @param {string} body.fieldId — attachment subdoc match key
 * @param {string} body.blobUrl — attachment subdoc match key
 * @param {'clean'|'infected'} body.scanStatus
 * @param {string} [body.reason] — operator-provided context (≤500 chars)
 */
export async function setAttachmentScanStatus(submissionId, body) {
  const { data } = await api.post(
    `/v2/workflow-forms/submissions/${encodeURIComponent(submissionId)}/scan-status`,
    body
  );
  return data;
}

/**
 * Submit a workflow form. Idempotent on `payload.clientUuid` —
 * server returns the prior submission with `idempotent: true` rather
 * than 409 when the same UUID is re-posted.
 *
 * @param {string} formIdOrSlug — 24-hex ObjectId or slug; slug
 *   resolves to the latest published version on the server.
 * @param {object} payload — see the 4.7 Zod schema. Required fields:
 *   `clientUuid` (UUIDv4), `values` (Record<string, unknown>).
 *   Optional: `attachments[]`, `customerId`, `personId`,
 *   `conversationId`, `status` ('submitted' | 'partial').
 * @param {object} [options]
 * @param {string} [options.idempotencyKey] — set the request-level
 *   `Idempotency-Key` header. The 2.19 outbox passes the same UUID
 *   here as in `payload.clientUuid`; new callers should follow the
 *   same convention so the Redis + Mongo dedup layers see a
 *   consistent key.
 * @returns {Promise<{ submission: object, idempotent: boolean }>}
 */
export async function submitWorkflowForm(
  formIdOrSlug,
  payload,
  { idempotencyKey } = {}
) {
  const config = idempotencyKey
    ? { headers: { 'Idempotency-Key': idempotencyKey } }
    : undefined;
  const { data } = await api.post(
    `/v2/workflow-forms/${encodeURIComponent(formIdOrSlug)}/submit`,
    payload,
    config
  );
  return data;
}

/**
 * Soft-delete a workflow form (recoverable, D5). Accepts a form `_id`
 * (any version) or a `slug`; the server removes ALL of that slug's live
 * versions so the form disappears from the list. Existing submissions
 * are left intact. businessadmin / staff only (403 otherwise). Returns
 * `{ deleted: true, slug, deletedCount, versions }`.
 */
export async function deleteWorkflowForm(formIdOrSlug) {
  const { data } = await api.delete(
    `/v2/workflow-forms/${encodeURIComponent(formIdOrSlug)}`
  );
  return data;
}

// ── 5.10 — tenant self-serve AI form authoring (Console) ───────────

/**
 * One conversational authoring turn (5.10 PR3). Sends the running
 * message list; returns `{ message, jsxSource, compiledSchema,
 * astErrors, warnings, compiled, tokensUsed, tokenBudget, quotaWarning }`.
 * businessadmin-only server-side (403 otherwise); 429 on the per-tenant
 * spend cap or per-session token budget.
 */
export async function authorWorkflowFormAi({ messages, currentJsx, business }) {
  const { data } = await api.post(
    '/v2/workflow-forms/author',
    {
      messages,
      ...(currentJsx ? { currentJsx } : {}),
      ...(business ? { business } : {}),
    },
    // Authoring is a multi-turn model call (interview + build + a possible
    // compile-repair round-trip) — it routinely runs longer than the
    // client's default 30s. The build turn of a large, multi-section form
    // on a slower model can take a couple of minutes, so 120s was killing
    // valid builds client-side mid-flight. Sit above the backend's
    // per-call ceiling (AUTHOR_MODEL_CALL_TIMEOUT_MS, 200s) so the server
    // returns a clean error first instead of the client guessing.
    { timeout: 240000 }
  );
  return data;
}

// Parse one raw SSE block ("event: x\ndata: {...}") into { event, data }.
function parseSseBlock(raw) {
  let event = 'message';
  const dataLines = [];
  for (const line of raw.split('\n')) {
    if (line.startsWith(':')) continue; // comment / heartbeat
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return null;
  let data = {};
  try {
    data = JSON.parse(dataLines.join('\n'));
  } catch {
    data = {};
  }
  return { event, data };
}

/**
 * Streaming variant of authorWorkflowFormAi (5.10 live). Hits the SSE
 * endpoint and surfaces the chat reply as it's written so the operator
 * watches the form build instead of waiting on a dead spinner.
 *
 * EventSource can't send an Authorization header (F.1), so we use fetch +
 * a ReadableStream reader with the same auth/tenant headers axios attaches.
 *
 * Callbacks: `onMessage(text)` — cumulative chat-reply snapshot;
 * `onPhase('building'|'repairing')`; `onProgress(fields)` — coarse count of
 * fields written so far (heartbeat while the large jsxSource streams).
 * Resolves with the SAME final payload
 * as authorWorkflowFormAi. On a pre-stream HTTP error (403/413/429/…) or
 * an in-stream `error` event, throws an Error with `.response = { status,
 * data }` so callers reuse their existing status-based handling.
 */
export async function authorWorkflowFormAiStream(
  { messages, currentJsx, business },
  { onMessage, onPhase, onProgress, signal } = {}
) {
  const path = '/v2/workflow-forms/author/stream';
  const res = await fetch(`${getApiBase()}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...buildTenantAuthHeaders(path),
    },
    body: JSON.stringify({
      messages,
      ...(currentJsx ? { currentJsx } : {}),
      ...(business ? { business } : {}),
    }),
    signal,
  });

  if (!res.ok || !res.body) {
    // Pre-stream failure — server replied with a normal JSON error.
    let data = {};
    try {
      data = await res.json();
    } catch {
      data = {};
    }
    const err = new Error(data.message || `Request failed (${res.status})`);
    err.response = { status: res.status, data };
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let finalResult = null;
  let streamError = null;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let nl;
    while ((nl = buf.indexOf('\n\n')) !== -1) {
      const block = buf.slice(0, nl);
      buf = buf.slice(nl + 2);
      const evt = parseSseBlock(block);
      if (!evt) continue;
      if (evt.event === 'message') onMessage?.(evt.data.text || '');
      else if (evt.event === 'phase') onPhase?.(evt.data.phase);
      else if (evt.event === 'progress') onProgress?.(evt.data.fields || 0);
      else if (evt.event === 'result') finalResult = evt.data;
      else if (evt.event === 'error') streamError = evt.data;
    }
  }

  if (streamError) {
    const err = new Error(
      streamError.message || 'The AI builder hit a snag. Please retry.'
    );
    err.response = { status: streamError.status || 500, data: streamError };
    throw err;
  }
  if (!finalResult) {
    throw new Error('The AI builder did not return a result. Please retry.');
  }
  return finalResult;
}

/**
 * Persist an AI-authored proposal as a tenant draft (5.10 PR4). The
 * server re-compiles `jsxSource` and validates `mapsTo`. Returns
 * `{ draft }`. 409 if the slug already exists.
 */
export async function createTenantDraft({
  slug,
  name,
  jsxSource,
  mapsTo,
  attachmentsConfig,
  autoReport,
}) {
  const { data } = await api.post('/v2/workflow-forms/drafts', {
    slug,
    name,
    jsxSource,
    ...(mapsTo ? { mapsTo } : {}),
    ...(attachmentsConfig ? { attachmentsConfig } : {}),
    ...(typeof autoReport === 'boolean' ? { autoReport } : {}),
  });
  return data;
}

/**
 * List this tenant's AI-authored drafts + their approval status
 * (5.10 PR4). Returns `{ drafts: [...] }`.
 */
export async function listMyDrafts() {
  const { data } = await api.get('/v2/workflow-forms/drafts');
  return data || { drafts: [] };
}

/**
 * Submit a draft into the staff approval queue (5.10 PR4). Returns
 * `{ draft }`. 409 if the draft isn't in a submittable state.
 */
export async function submitDraftForApproval(id) {
  const { data } = await api.post(
    `/v2/workflow-forms/drafts/${encodeURIComponent(id)}/submit-for-approval`
  );
  return data;
}
